<template>
  <div class="cb-card">

    <!-- Header -->
    <div class="cb-header">
      <div class="cb-header-left">
        <div class="cb-icon-wrap">
          <i :class="icon"></i>
        </div>
        <span class="cb-title">{{ title }}</span>
      </div>

      <!-- Type toggle (sauf forcePro) -->
      <div v-if="!forcePro" class="cb-type-toggle">
        <button
          v-for="opt in typeOptions"
          :key="opt.value"
          :class="['cb-type-btn', { active: client.type_client === opt.value }]"
          @click="client.type_client = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <div v-else class="cb-type-badge">
        <i class="fas fa-briefcase"></i> Professionnel
      </div>
    </div>

    <!-- Body -->
    <div class="cb-body">

      <!-- Particulier : Nom + Prénom -->
      <template v-if="client.type_client === 'particulier'">
        <div class="cb-row">
          <div class="cb-field">
            <label class="cb-label" :for="`${idPrefix}-nom`">Nom</label>
            <div class="cb-input-wrap">
              <input :id="`${idPrefix}-nom`" v-model="client.nom" type="text" placeholder="Dupont" class="cb-input" />
            </div>
          </div>
          <div class="cb-field">
            <label class="cb-label" :for="`${idPrefix}-prenom`">Prénom</label>
            <div class="cb-input-wrap">
              <input :id="`${idPrefix}-prenom`" v-model="client.prenom" type="text" placeholder="Jean" class="cb-input" />
            </div>
          </div>
        </div>
      </template>

      <!-- Professionnel : Société + SIRET -->
      <template v-else>
        <div class="cb-field cb-field--full">
          <label class="cb-label" :for="`${idPrefix}-societe`">Société</label>
          <div class="cb-input-wrap">
            <input :id="`${idPrefix}-societe`" v-model="client.nom" type="text" placeholder="Exemple SARL" class="cb-input" />
          </div>
        </div>
        <div class="cb-field cb-field--full">
          <label class="cb-label" :for="`${idPrefix}-siret`">
            SIRET
            <span class="cb-label-hint">14 chiffres</span>
          </label>
          <div class="cb-input-wrap">
            <input
              :id="`${idPrefix}-siret`"
              :value="client.siret"
              @input="onSiretInput"
              @keydown="onSiretKeydown"
              type="text"
              inputmode="numeric"
              placeholder="12345678900012"
              class="cb-input"
              :class="{ 'is-valid': client.siret && client.siret.length === 14 }"
            />
            <div v-if="client.siret && client.siret.length === 14" class="cb-input-check">
              <i class="fas fa-check"></i>
            </div>
          </div>
        </div>
      </template>

      <!-- Téléphone + Email -->
      <div class="cb-row">
        <div class="cb-field">
          <label class="cb-label" :for="`${idPrefix}-telephone`">Téléphone</label>
          <div class="cb-input-wrap">
            <input
              :id="`${idPrefix}-telephone`"
              :value="phoneDisplay"
              @input="onPhoneInput"
              @keydown="onPhoneKeydown"
              type="tel"
              inputmode="numeric"
              placeholder="06 12 34 56 78"
              class="cb-input"
              :class="{ 'is-valid': phoneValid }"
            />
            <div v-if="phoneValid" class="cb-input-check">
              <i class="fas fa-check"></i>
            </div>
          </div>
        </div>
        <div class="cb-field">
          <label class="cb-label" :for="`${idPrefix}-email`">Email</label>
          <div class="cb-input-wrap">
            <input
              :id="`${idPrefix}-email`"
              v-model="client.email"
              type="email"
              :placeholder="client.type_client === 'particulier' ? 'jean@gmail.com' : 'contact@societe.fr'"
              class="cb-input"
            />
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
function emptyClient(overrides = {}) {
  return {
    type            : '',
    type_client     : 'particulier',
    nom             : '',
    prenom          : '',
    siret           : '',
    telephone       : '',
    email           : '',
    regie_id        : 0,
    proprietaire_id : 0,
    ...overrides,
  }
}

export default {
  name: 'ClientBlock',

  props: {
    title    : { type: String,  required: true },
    icon     : { type: String,  required: true },
    value    : { type: Object,  default: () => ({}) },
    forcePro : { type: Boolean, default: false },
    idPrefix : { type: String, required: true },
  },

  data() {
    return {
      client     : { ...emptyClient(), ...this.value },
      rawDigits  : '',
      typeOptions: [
        { value: 'particulier',   label: 'Particulier' },
        { value: 'professionnel', label: 'Pro' },
      ],
    }
  },

  computed: {
    phoneDisplay() {
      return this.rawDigits.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
    },
    phoneValid() {
      return this.rawDigits.length === 10
    },
  },

  watch: {
    value: {
      deep: true,
      handler(v) {
        if (JSON.stringify(v) !== JSON.stringify(this.client)) {
          this.client = { ...emptyClient(), ...v }
          this.parseIncomingPhone(v.telephone)
        }
      }
    },
    client: {
      deep: true,
      handler(v) {
        if (JSON.stringify(v) !== JSON.stringify(this.value))
          this.$emit('input', { ...v })
      }
    },
    rawDigits(val) {
      this.client.telephone = val
    },
  },

  mounted() {
    if (this.forcePro) this.client.type_client = 'professionnel'
    this.parseIncomingPhone(this.client.telephone)
  },

  methods: {
    parseIncomingPhone(tel) {
      if (!tel) { this.rawDigits = ''; return }
      this.rawDigits = tel.replace(/\D/g, '').slice(0, 10)
    },

    onPhoneInput(e) {
      this.rawDigits = e.target.value.replace(/\D/g, '').slice(0, 10)
    },

    onPhoneKeydown(e) {
      const ctrl = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End']
      if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return
      if (!/^\d$/.test(e.key)) { e.preventDefault(); return }
      if (this.rawDigits.length >= 10) e.preventDefault()
    },

    onSiretInput(e) {
      this.client.siret = e.target.value.replace(/\D/g, '').slice(0, 14)
    },

    onSiretKeydown(e) {
      const ctrl = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End']
      if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return
      if (!/^\d$/.test(e.key)) { e.preventDefault(); return }
      if (this.client.siret && this.client.siret.length >= 14) e.preventDefault()
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

.cb-card {
  font-family: 'DM Sans', sans-serif;
  background: #fff;
  border: 1.5px solid #e8e8ed;
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s;
}

/* ── Header ── */
.cb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f4;
  gap: 10px;
  flex-wrap: wrap;
}

.cb-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cb-icon-wrap {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: #fff1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c8102e;
  font-size: 11px;
}

.cb-title {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b6b78;
}

/* ── Type toggle ── */
.cb-type-toggle {
  display: flex;
  gap: 3px;
  background: #f0f0f4;
  border-radius: 8px;
  padding: 3px;
}

.cb-type-btn {
  padding: 4px 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #9898a6;
  cursor: pointer;
  transition: all 0.15s;
}

.cb-type-btn.active {
  background: #fff;
  color: #c8102e;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.cb-type-badge {
  font-size: 11px;
  font-weight: 500;
  color: #c8102e;
  background: #fff1f2;
  border-radius: 6px;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── Body ── */
.cb-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cb-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.cb-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cb-field--full {
  grid-column: span 2;
}

.cb-label {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #aaaab6;
  display: flex;
  align-items: center;
  gap: 5px;
}

.cb-label-hint {
  font-size: 10px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #c8c8d4;
}

/* ── Inputs ── */
.cb-input-wrap {
  position: relative;
}

.cb-input {
  width: 100%;
  padding: 9px 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: #1a1a2e;
  background: #fafafa;
  border: 1.5px solid #e8e8ed;
  border-radius: 9px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.cb-input::placeholder {
  color: #c8c8d4;
}

.cb-input:focus {
  background: #fff;
  border-color: #c8102e;
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.07);
}

.cb-input.is-valid {
  border-color: #22c55e;
  padding-right: 34px;
}

.cb-input-check {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #22c55e;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.cb-input-check i {
  color: #fff;
  font-size: 9px;
}
</style>